import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import useGetPartnerConfigByDomain from '~/features/partners/hooks/useGetPartnerConfigByDomain'
import useGetPartnerConfigByOrgId from '~/features/partners/hooks/useGetPartnerConfigByOrgId'
import { TPartner } from '~/features/partners/models'
import useGetAuthSessionQuery from '~/hooks/useGetAuthSessionQuery'
import { EAdminPortalClientPath, EMarketingClientPath } from '~/models/api'
import { sessionStore, store } from '~/store'

type TOptions = {
  shouldFetch: boolean
}

const usePartnerConfigQueries = ({ shouldFetch }: TOptions) => {
  const location = useLocation()
  const user = store((state) => state.user)

  const isLoadingUser = sessionStore((state) => state.UIElements.isLoadingUser)

  const { isSuccess: isSuccessAuthData } = useGetAuthSessionQuery()

  const isNonPartnerPath = useMemo(() => {
    const isAdminPortalPartnerConfigPath = [
      EAdminPortalClientPath.portalGeneralSettings,
      EAdminPortalClientPath.portalBranding,
    ].includes(location.pathname as EAdminPortalClientPath)

    if (isAdminPortalPartnerConfigPath) {
      return false
    }

    return (
      Object.values(EMarketingClientPath).includes(
        location.pathname as EMarketingClientPath,
      ) ||
      Object.values(EAdminPortalClientPath).includes(
        location.pathname as EAdminPortalClientPath,
      )
    )
  }, [location.pathname])

  const shouldGetPartnerByOrgId = !(isNonPartnerPath || !user.organizationId)

  const orgIdForQuery = shouldGetPartnerByOrgId ? user.organizationId : ''

  const {
    data: partnerByOrgIdResponse,
    isError: isErrorByOrgId,
    isLoading: isLoadingByOrgId,
  } = useGetPartnerConfigByOrgId({
    orgId: orgIdForQuery,
    options: { shouldFetch },
  })

  const shouldGetPartnerByDomain =
    !isNonPartnerPath &&
    (isErrorByOrgId ||
      isLoadingUser ||
      (!isLoadingByOrgId &&
        !partnerByOrgIdResponse?.data &&
        !user.organizationId))

  const { data: partnerDomainResponse, isLoading: isLoadingByDomain } =
    useGetPartnerConfigByDomain({
      shouldLoadByDomain: shouldGetPartnerByDomain,
      options: { shouldFetch },
    })

  const isLoadingPartnerByOrgId = shouldGetPartnerByOrgId && isLoadingByOrgId
  const isLoadingPartnerByDomain = shouldGetPartnerByDomain && isLoadingByDomain

  const partner = useMemo((): TPartner | null => {
    if (isNonPartnerPath) {
      return null
    }

    return partnerByOrgIdResponse?.data ?? partnerDomainResponse?.data ?? null
  }, [
    isNonPartnerPath,
    partnerByOrgIdResponse?.data,
    partnerDomainResponse?.data,
  ])

  const isLoading =
    !isSuccessAuthData ||
    isLoadingUser ||
    isLoadingPartnerByOrgId ||
    isLoadingPartnerByDomain

  return { isLoading, partner }
}

export default usePartnerConfigQueries
